<template>
    <div class="relative" ref="dropdownContainer">
        <label :for="name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ label }}
        </label>

        <div class="relative">
            <!-- Custom dropdown trigger -->
            <button
                type="button"
                :id="name"
                @click="toggleDropdown"
                :disabled="loading || flights.length === 0"
                class="block w-full px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base text-left bg-white"
                :class="{
                    'bg-gray-100 cursor-not-allowed': loading || flights.length === 0,
                    'border-red-500': error
                }"
            >
                <!-- Selected flight display -->
                <div v-if="selectedFlight" class="flex items-center gap-2">
                    <img
                        :src="getAirlineLogo(selectedFlight.airline_iata)"
                        :alt="selectedFlight.airline_name"
                        class="w-6 h-4 object-contain flex-shrink-0"
                        @error="onLogoError"
                    >
                    <span class="truncate">{{ formatFlightOption(selectedFlight) }}</span>
                </div>
                <span v-else class="text-gray-500">
                    {{ loading ? 'Loading flights...' :
                       flights.length === 0 ? 'No flights available' :
                       placeholder || 'Select a flight' }}
                </span>
                <!-- Chevron -->
                <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                </svg>
            </button>

            <!-- Loading indicator -->
            <div v-if="loading" class="absolute right-8 top-1/2 transform -translate-y-1/2">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>

            <!-- Dropdown panel -->
            <div
                v-if="isOpen"
                class="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-[32rem] overflow-hidden"
            >
                <!-- Search input -->
                <div class="p-2 border-b border-gray-200">
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search by flight number, airline..."
                        class="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        @keydown.escape="closeDropdown"
                    >
                </div>

                <!-- Flight options list -->
                <ul class="max-h-96 overflow-y-auto">
                    <li v-if="filteredFlights.length === 0" class="px-3 py-3 text-base text-gray-500 text-center">
                        No flights match your search
                    </li>
                    <li
                        v-for="flight in filteredFlights"
                        :key="getFlightKey(flight)"
                        @click="selectFlight(flight)"
                        class="flex items-center gap-3 px-3 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        :class="{ 'bg-blue-50': selectedFlight && getFlightKey(flight) === getFlightKey(selectedFlight) }"
                    >
                        <img
                            :src="getAirlineLogo(flight.airline_iata)"
                            :alt="flight.airline_name"
                            class="w-10 h-6 object-contain flex-shrink-0"
                            @error="onLogoError"
                        >
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2 text-lg">
                                <span class="font-bold text-gray-900">{{ flight.flight_iata || flight.flight_icao || 'N/A' }}</span>
                                <span class="text-gray-600 truncate">{{ flight.airline_name }}</span>
                            </div>
                            <div class="text-sm text-gray-600 mt-1">
                                {{ flight.dep_iata }} → {{ flight.arr_iata }}
                                <span class="ml-2 text-gray-800 font-semibold">{{ flight.formatted_dep_time || 'Unknown time' }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Error message -->
        <p v-if="error" class="mt-1 text-sm text-red-600">{{ error }}</p>

        <!-- Flight details for selected flight -->
        <div v-if="selectedFlight" class="mt-2 p-3 bg-gray-50 rounded-md text-base">
            <div class="flex items-center gap-3">
                <img
                    :src="getAirlineLogo(selectedFlight.airline_iata)"
                    :alt="selectedFlight.airline_name"
                    class="w-10 h-7 object-contain flex-shrink-0"
                    @error="onLogoError"
                >
                <div class="flex-1">
                    <div class="font-semibold text-gray-900">
                        {{ selectedFlight.flight_iata || selectedFlight.flight_icao }}
                        <span class="text-gray-500 ml-2">{{ selectedFlight.airline_name }}</span>
                    </div>
                    <div class="text-gray-600 mt-1">
                        {{ selectedFlight.dep_iata }} → {{ selectedFlight.arr_iata }}
                    </div>
                    <div class="text-gray-500 mt-1">
                        <span v-if="selectedFlight.formatted_dep_time">Dep: {{ selectedFlight.formatted_dep_time }}</span>
                        <span v-if="selectedFlight.formatted_arr_time" class="ml-3">Arr: {{ selectedFlight.formatted_arr_time }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'FlightDropdown',
    props: {
        label: {
            type: String,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        placeholder: {
            type: String,
            default: ''
        },
        modelValue: {
            type: String,
            default: ''
        },
        flightType: {
            type: String,
            required: true,
            validator: value => ['outbound', 'inbound'].includes(value)
        },
        entryTime: {
            type: String,
            default: null
        },
        exitTime: {
            type: String,
            default: null
        },
        destinationCode: {
            type: String,
            default: null
        },
        autoLoad: {
            type: Boolean,
            default: true
        },
        error: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            flights: [],
            selectedFlightId: this.modelValue,
            selectedFlight: null,
            loading: false,
            isOpen: false,
            searchQuery: '',
        };
    },
    computed: {
        filteredFlights() {
            if (!this.searchQuery.trim()) {
                return this.flights;
            }
            const query = this.searchQuery.toLowerCase().trim();
            return this.flights.filter(flight => {
                const flightIata = (flight.flight_iata || '').toLowerCase();
                const flightIcao = (flight.flight_icao || '').toLowerCase();
                const flightNumber = (flight.flight_number || '').toString().toLowerCase();
                const airlineName = (flight.airline_name || '').toLowerCase();
                const airlineIata = (flight.airline_iata || '').toLowerCase();
                const depIata = (flight.dep_iata || '').toLowerCase();
                const arrIata = (flight.arr_iata || '').toLowerCase();

                return flightIata.includes(query)
                    || flightIcao.includes(query)
                    || flightNumber.includes(query)
                    || airlineName.includes(query)
                    || airlineIata.includes(query)
                    || depIata.includes(query)
                    || arrIata.includes(query);
            });
        }
    },
    watch: {
        modelValue(newValue) {
            this.selectedFlightId = newValue;
            this.updateSelectedFlight();
        },
        entryTime() {
            if (this.flightType === 'outbound' && this.autoLoad) {
                this.loadFlights();
            }
        },
        exitTime() {
            if (this.flightType === 'inbound' && this.autoLoad) {
                this.loadFlights();
            }
        },
        destinationCode() {
            if (this.flightType === 'inbound' && this.destinationCode && this.autoLoad) {
                this.loadFlights();
            }
        }
    },
    mounted() {
        if (this.autoLoad) {
            this.loadFlights();
        }
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            if (this.loading || this.flights.length === 0) return;
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.searchQuery = '';
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus();
                });
            }
        },

        closeDropdown() {
            this.isOpen = false;
            this.searchQuery = '';
        },

        handleClickOutside(event) {
            if (this.$refs.dropdownContainer && !this.$refs.dropdownContainer.contains(event.target)) {
                this.closeDropdown();
            }
        },

        selectFlight(flight) {
            this.selectedFlightId = this.getFlightKey(flight);
            this.selectedFlight = flight;
            this.$emit('update:modelValue', this.selectedFlightId);
            this.$emit('flight-selected', flight);

            if (this.flightType === 'outbound' && flight) {
                this.$emit('destination-changed', flight.arr_iata);
            }

            this.closeDropdown();
        },

        async loadFlights() {
            if (this.flightType === 'outbound' && !this.entryTime) {
                return;
            }

            if (this.flightType === 'inbound' && (!this.exitTime || !this.destinationCode)) {
                return;
            }

            this.loading = true;
            this.flights = [];

            try {
                let response;

                if (this.flightType === 'outbound') {
                    response = await axios.post('/api/flights/outbound', {
                        entry_time: this.entryTime
                    });
                } else {
                    response = await axios.post('/api/flights/inbound', {
                        destination_code: this.destinationCode,
                        exit_time: this.exitTime
                    });
                }

                if (response.data.success) {
                    this.flights = response.data.flights;
                    this.updateSelectedFlight();
                }
            } catch (error) {
                console.error('Error loading flights:', error);
                this.$emit('error', 'Failed to load flights');
            } finally {
                this.loading = false;
            }
        },

        updateSelectedFlight() {
            if (this.selectedFlightId) {
                this.selectedFlight = this.flights.find(flight =>
                    this.getFlightKey(flight) === this.selectedFlightId
                );
            } else {
                this.selectedFlight = null;
            }
        },

        getFlightKey(flight) {
            return `${flight.flight_iata || flight.flight_icao}_${flight.dep_time}`;
        },

        formatFlightOption(flight) {
            const time = flight.formatted_dep_time || 'Unknown time';
            const route = `${flight.dep_iata} → ${flight.arr_iata}`;
            const flightCode = flight.flight_iata || flight.flight_icao || 'N/A';
            return `${flightCode} - ${route} at ${time}`;
        },

        getAirlineLogo(airlineIata) {
            if (!airlineIata) return '';
            return `https://pics.avs.io/60/30/${airlineIata}.png`;
        },

        onLogoError(event) {
            event.target.style.display = 'none';
        },

        refresh() {
            this.loadFlights();
        }
    }
};
</script>

<style scoped>
.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
